import React from 'react';
import Head from 'next/head';
import { useDevice } from '@bonnet/next/device';
import { Hero } from 'reaxl-organisms';
import { useOptimizelyTrack } from 'reaxl-optimizely';
import { BrowseByLinks, SearchBadge, SearchBox } from '@/component';
import { linksPreload } from '@/utilities';
import { homepageContent } from '@/reference';
import { SuperHeroAdBarEnhanced } from './hp';
import { handleHomePageKeywordSearchSubmit } from '@/actions/heroSearchActions';

const getSuperHeroPreloadSrc = (mediaType, content) => {
    const { image, imageSs, imageSm, imageMd } = content;

    switch (mediaType) {
        case 'xs':
            return imageSs;
        case 'sm':
            return imageSm;
        case 'md':
            return imageMd;
        default:
            return image;
    }
};

const RefreshedHero = React.memo(
    ({
        headingOneTitle,
        userZipCode,
        styleCodes,
        dealerId,
        isRefreshedHeroEnabled,
        enableRefreshedHeroVariantA,
        enableRefreshedHeroVariantB,
    }) => {
        const isControl = isRefreshedHeroEnabled
            && !(enableRefreshedHeroVariantA || enableRefreshedHeroVariantB);
        const content = isControl
            ? homepageContent.heroRefreshControl
            : homepageContent.heroRefreshChallenger;

        const optimizelyTrack = useOptimizelyTrack();
        const device = useDevice();

        const isMobile = device?.is.xs;
        const mediaType = device?.mediaType;

        const superHeroPreloadSrc = getSuperHeroPreloadSrc(mediaType, content);

        const handleKeywordSearchSubmitCallback = (data = {}) => {
            optimizelyTrack('hp_search_click');
            handleHomePageKeywordSearchSubmit({ ...data });
        };

        return (
            <>
                <Head>
                    {superHeroPreloadSrc
                        && linksPreload.preImage(superHeroPreloadSrc)}
                </Head>

                <div className="refresh">
                    {isControl ? (
                        <Hero
                            uiContext="superhero"
                            block
                            src={homepageContent.heroRefreshControl.image}
                            lgSrc={homepageContent.heroRefreshControl.image}
                            mdSrc={homepageContent.heroRefreshControl.imageMd}
                            smSrc={homepageContent.heroRefreshControl.imageSm}
                            xsSrc={homepageContent.heroRefreshControl.imageSs}
                        >
                            <SearchBadge
                                className="col-sm-12 col-md-10 col-md-offset-1 margin-top-4 text-inverse padding-left-3 padding-right-3"
                                badgeTitle={headingOneTitle}
                                labelClassName="text-inverse"
                                titleClassName="text-bold text-size-500"
                                makeConfig={{
                                    analyticsData: { pageType: 'homepage' },
                                }}
                                modelConfig={{
                                    analyticsData: { pageType: 'homepage' },
                                }}
                                onKeywordSearchFormSubmit={handleKeywordSearchSubmitCallback}
                                browseByLinks={(
                                    <BrowseByLinks
                                        enableBrowseByBudgetLink
                                        classNames="text-inverse"
                                        listingType="used"
                                        styleCodes={styleCodes}
                                        analyticsPageType="homepage"
                                        zipCode={userZipCode}
                                        dealerId={dealerId}
                                    />
                                )}
                            />
                            <SuperHeroAdBarEnhanced
                                isHomepage
                                superHeroContent={homepageContent.heroRefreshControl}
                                isMobile={isMobile}
                                isEnhanced
                                handleButtonClick={() => {}}
                                handleLogoClick={() => {}}
                            />
                        </Hero>
                    ) : (
                        <div className="refreshed-hero">
                            <Hero
                                uiContext="superhero"
                                contentPosition="topCenter"
                                block
                                src={homepageContent.heroRefreshChallenger.image}
                                lgSrc={homepageContent.heroRefreshChallenger.image}
                                mdSrc={homepageContent.heroRefreshChallenger.imageMd}
                                smSrc={homepageContent.heroRefreshChallenger.imageSm}
                                xsSrc={homepageContent.heroRefreshChallenger.imageSs}
                            />
                            <SearchBox
                                isMobile={isMobile}
                                device={device}
                                zipCode={userZipCode}
                                dealerId={dealerId}
                                styleCodes={styleCodes}
                                enableRefreshedHeroVariantA={enableRefreshedHeroVariantA}
                                enableRefreshedHeroVariantB={enableRefreshedHeroVariantB}
                                ctaText={homepageContent.heroRefreshChallenger.ctaText}
                            />
                        </div>
                    )}
                </div>
            </>
        );
    },
);

export default RefreshedHero;
